/**
 * HR Branches — จัดการสาขา (multi-branch). แต่ละสาขามี geofence ของตัวเอง
 * (latitude/longitude/radius_meters) แยกจากพิกัดออฟฟิศกลางที่ /hr/office-location.
 * ลอกวิธีจับพิกัดปัจจุบันจาก OfficeGeofence.tsx (navigator.geolocation).
 */
import { useState } from 'react';
import { useAuthStore } from '../../lib/store';
import { Store, Plus, Pencil, XCircle, CheckCircle2, MapPin, MapPinOff } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { Modal } from '../../components/ui/Modal';
import { useBranches, useCreateBranch, useUpdateBranch, useDeleteBranch, type Branch } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

interface BranchFormState {
  name: string;
  code: string;
  address: string;
  phone: string;
  latitude: string;
  longitude: string;
  radius_meters: string;
}

const EMPTY_FORM: BranchFormState = {
  name: '',
  code: '',
  address: '',
  phone: '',
  latitude: '',
  longitude: '',
  radius_meters: '200',
};

function toForm(b: Branch): BranchFormState {
  return {
    name: b.name ?? '',
    code: b.code ?? '',
    address: b.address ?? '',
    phone: b.phone ?? '',
    latitude: b.latitude != null ? String(b.latitude) : '',
    longitude: b.longitude != null ? String(b.longitude) : '',
    radius_meters: b.radius_meters != null ? String(b.radius_meters) : '200',
  };
}

function BranchModal({ initial, onClose }: { initial: Branch | null; onClose: () => void }) {
  const isEdit = !!initial;
  const create = useCreateBranch();
  const update = useUpdateBranch();
  const toast = useToast();
  const [form, setForm] = useState<BranchFormState>(initial ? toForm(initial) : EMPTY_FORM);
  const [geoLoading, setGeoLoading] = useState(false);

  function setField(key: keyof BranchFormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error('อุปกรณ์นี้ไม่รองรับการอ่านตำแหน่ง');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('latitude', pos.coords.latitude.toFixed(6));
        setField('longitude', pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED ? 'ไม่ได้รับอนุญาตให้เข้าถึงตำแหน่ง — เปิดสิทธิ์ตำแหน่งในเบราว์เซอร์แล้วลองใหม่' :
          err.code === err.POSITION_UNAVAILABLE ? 'หาตำแหน่งไม่ได้ ลองใหม่อีกครั้ง' :
          err.code === err.TIMEOUT ? 'อ่านตำแหน่งนานเกินไป ลองใหม่อีกครั้ง' :
          'อ่านตำแหน่งไม่สำเร็จ';
        toast.error(msg);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  const saving = create.isPending || update.isPending;

  async function save() {
    if (!form.name.trim()) {
      toast.error('กรอกชื่อสาขาก่อน');
      return;
    }
    if (form.radius_meters !== '') {
      const n = Number(form.radius_meters);
      if (!Number.isFinite(n) || n < 20 || n > 5000) {
        toast.error('รัศมี geofence ต้องอยู่ระหว่าง 20–5000 เมตร');
        return;
      }
    }
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      code: form.code.trim() || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      latitude: form.latitude !== '' ? Number(form.latitude) : null,
      longitude: form.longitude !== '' ? Number(form.longitude) : null,
      radius_meters: form.radius_meters !== '' ? Number(form.radius_meters) : null,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: initial!.id, ...body });
      } else {
        await create.mutateAsync(body);
      }
      toast.success(isEdit ? 'บันทึกสาขาแล้ว' : 'เพิ่มสาขาแล้ว');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? `แก้ไขสาขา — ${initial!.name}` : 'เพิ่มสาขา'}>
      <div className="space-y-3">
        <div>
          <label className="label">ชื่อสาขา *</label>
          <input className="input" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="เช่น สุกรีเภสัช สาขาสีลม" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">รหัสสาขา</label>
            <input className="input" value={form.code} onChange={(e) => setField('code', e.target.value)} placeholder="เช่น SL01" />
          </div>
          <div>
            <label className="label">เบอร์โทร</label>
            <input className="input" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="0812345678" />
          </div>
        </div>
        <div>
          <label className="label">ที่อยู่</label>
          <textarea className="input" rows={2} value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="ที่อยู่สาขา" />
        </div>

        <div className="border-t border-gray-100 pt-3">
          <div className="font-semibold text-sm mb-1">พื้นที่ตรวจเช็คอิน (geofence)</div>
          <p className="text-xs text-gray-500 mb-2">ให้ยืนที่หน้าร้านสาขานี้แล้วกดปุ่มด้านล่าง — ระบบจะจับพิกัดปัจจุบันมาใส่ให้อัตโนมัติ</p>
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={geoLoading}
            className="btn-secondary text-sm w-full justify-center mb-2"
          >
            <MapPin className="w-4 h-4" /> {geoLoading ? 'กำลังอ่านตำแหน่ง…' : '📍 ใช้ตำแหน่งปัจจุบัน'}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Latitude</label>
              <input className="input" value={form.latitude} onChange={(e) => setField('latitude', e.target.value)} placeholder="เช่น 13.7563" />
            </div>
            <div>
              <label className="label">Longitude</label>
              <input className="input" value={form.longitude} onChange={(e) => setField('longitude', e.target.value)} placeholder="เช่น 100.5018" />
            </div>
          </div>
          <div className="mt-2">
            <label className="label">รัศมีที่อนุญาต (เมตร)</label>
            <input type="number" min={20} max={5000} className="input" value={form.radius_meters} onChange={(e) => setField('radius_meters', e.target.value)} placeholder="200" />
          </div>
          {form.latitude !== '' && form.longitude !== '' && (
            <button
              type="button"
              onClick={() => { setField('latitude', ''); setField('longitude', ''); }}
              className="text-xs text-gray-400 hover:text-red-600 mt-1"
            >
              ล้างพิกัด
            </button>
          )}
        </div>

        <button onClick={save} disabled={saving} className="btn-primary w-full">
          {saving ? 'กำลังบันทึก…' : isEdit ? 'บันทึก' : 'เพิ่มสาขา'}
        </button>
      </div>
    </Modal>
  );
}

export function HRBranches() {
  // 🚨 หน้านี้เปิดให้ผู้จัดการสาขา "อ่าน" (ต้องรู้ข้อมูลสาขาตัวเอง + ใช้เป็นตัวกรอง
  // ในหน้าลงเวลา/ทะเบียน) แต่การเพิ่ม/แก้/ปิดสาขาเป็นงานสำนักงานใหญ่ — API กั้นไว้แล้ว
  // ถ้ายังโชว์ปุ่ม เขาจะกรอกฟอร์มจนจบแล้วเจอ 403 ตอนกดบันทึก
  const role = useAuthStore((s) => s.profile?.role);
  const isHq = role === 'OWNER' || role === 'HR';
  const { data: branches, isLoading } = useBranches();
  const del = useDeleteBranch();
  const update = useUpdateBranch();
  const toast = useToast();
  const confirm = useConfirm();
  const [modalFor, setModalFor] = useState<Branch | 'new' | null>(null);

  const sorted = [...(branches ?? [])].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return a.name.localeCompare(b.name, 'th');
  });

  async function handleClose(b: Branch) {
    const ok = await confirm({
      title: `ปิดสาขา "${b.name}"?`,
      message: 'พนักงานที่ตั้งสาขานี้เป็นสาขาหลักจะยังอยู่ในระบบ แต่สาขานี้จะไม่ขึ้นให้เลือกอีก — เปิดใช้งานกลับได้ภายหลัง',
      confirmText: 'ปิดสาขา',
      danger: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(b.id);
      toast.success('ปิดสาขาแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ปิดสาขาไม่สำเร็จ');
    }
  }

  async function handleReopen(b: Branch) {
    try {
      await update.mutateAsync({ id: b.id, is_active: true });
      toast.success('เปิดใช้งานสาขาแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดใช้งานไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="สาขา">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">จัดการรายชื่อสาขาและพื้นที่ตรวจเช็คอินของแต่ละสาขา</p>
        {isHq && (
          <button onClick={() => setModalFor('new')} className="btn-primary btn-slim shrink-0">
            <Plus className="w-3.5 h-3.5" /> เพิ่มสาขา
          </button>
        )}
      </div>

      {isLoading ? (
        <Loading />
      ) : sorted.length === 0 ? (
        <div className="card p-8 text-center text-gray-400">
          <Store className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">{isHq ? 'ยังไม่มีสาขา — กด "เพิ่มสาขา" เพื่อเริ่มต้น' : 'ยังไม่มีสาขาในระบบ'}</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {sorted.map((b) => {
            const hasGeo = b.latitude != null && b.longitude != null;
            return (
              <div key={b.id} className={`flex items-center gap-3 p-3 ${!b.is_active ? 'opacity-60' : ''}`}>
                <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                  <Store className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-1.5">
                    {b.name}
                    {b.code && <span className="text-xs text-gray-400 font-normal">({b.code})</span>}
                    {!b.is_active && <span className="badge bg-gray-100 text-gray-500 text-[10px]">ปิดใช้งาน</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{b.address || '—'}{b.phone ? ` · ${b.phone}` : ''}</div>
                  <div className={`text-[11px] flex items-center gap-1 mt-0.5 ${hasGeo ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {hasGeo ? <MapPin className="w-3 h-3" /> : <MapPinOff className="w-3 h-3" />}
                    {hasGeo ? `ตั้งพิกัดแล้ว (รัศมี ${b.radius_meters ?? 200} ม.)` : 'ยังไม่ตั้งพิกัด'}
                  </div>
                </div>
                <div className={`flex items-center gap-1 shrink-0 ${isHq ? '' : 'hidden'}`}>
                  <button onClick={() => setModalFor(b)} className="p-1.5 text-gray-400 hover:text-brand-600" title="แก้ไข">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {b.is_active ? (
                    <button onClick={() => handleClose(b)} className="p-1.5 text-gray-400 hover:text-red-600" title="ปิดสาขา">
                      <XCircle className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => handleReopen(b)} className="p-1.5 text-gray-400 hover:text-emerald-600" title="เปิดใช้งาน">
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalFor && (
        <BranchModal
          initial={modalFor === 'new' ? null : modalFor}
          onClose={() => setModalFor(null)}
        />
      )}
    </AdminLayout>
  );
}
